import upperFirst from "lodash/upperFirst";
import {
  MatchDocumentAmountToleranceModeEnum,
  MatchDocumentDateBasisEnum,
  MatchDocumentSettlementBasisEnum,
  MatchDocumentTextMatchModeEnum,
  type RuleDefinitionSuggestionMatchDocumentFragment,
} from "@/__generated__/graphql";
import {
  MATCH_DATE_BASIS_OPTIONS,
  TEXT_MATCH_MODE_OPTIONS,
} from "@/app/app/_/views/RulesView/constants/matchDocumentFields";
import { Utils } from "@/utils";

import type { ChangelistProposalPresentation } from "./TriageChangelistProposalRow";
import { amountDeltaFromCompare } from "./TriageSuggestionsPanel";

type LinkedDocument = ChangelistProposalPresentation["linkedDocuments"][number];

const MATCH_RESULT_CRITERIA = new Set(["amount", "date_window", "wording"]);

function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: unknown,
): string {
  const normalized = String(value ?? "");
  return (
    options.find((option) => option.value === normalized)?.label ?? normalized
  );
}

function formatToleranceValue(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string | null {
  const tolerance = definition.amountTolerance;
  if (tolerance == null) {
    return null;
  }
  if (
    definition.amountToleranceMode ===
    MatchDocumentAmountToleranceModeEnum.Percent
  ) {
    return `${Number(tolerance)}%`;
  }
  return Utils.fCurrency(Number(tolerance), false, true);
}

function amountWithinTolerance(
  delta: number,
  bankAmount: number,
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): boolean {
  const tolerance = definition.amountTolerance;
  if (tolerance == null) {
    return true;
  }
  const absDelta = Math.abs(delta);
  if (
    definition.amountToleranceMode ===
    MatchDocumentAmountToleranceModeEnum.Percent
  ) {
    const limit = (Math.abs(bankAmount) * Number(tolerance)) / 100;
    return absDelta <= limit + 0.005;
  }
  return absDelta <= Number(tolerance) + 0.005;
}

/**
 * What a document is compared by, in its own currency - and in the payment's,
 * turned at the official rate where the two differ.
 */
function documentCompareAmount(
  document: LinkedDocument,
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): { own: number; inPayment: number } | null {
  const bases = new Set(definition.settlementBases ?? []);
  const usesBalance =
    bases.has(MatchDocumentSettlementBasisEnum.Balance) ||
    bases.has(MatchDocumentSettlementBasisEnum.BalancePartial);
  const raw =
    usesBalance && document.amountDue != null
      ? document.amountDue
      : (document.amountTotal ?? document.amountDue ?? null);
  if (raw == null) {
    return null;
  }
  const own = Math.abs(Number(raw));
  return { own, inPayment: own * (document.exchangeRate ?? 1) };
}

function documentCompareDate(
  document: LinkedDocument,
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): { date: Date | null; basisLabel: string } {
  const bases = definition.matchDateBasis ?? [
    MatchDocumentDateBasisEnum.Issued,
  ];
  const primary = bases.filter(
    (basis) => basis !== MatchDocumentDateBasisEnum.UploadDate,
  );
  const ordered =
    primary.length > 0 ? primary : [MatchDocumentDateBasisEnum.UploadDate];

  for (const basis of ordered) {
    if (basis === MatchDocumentDateBasisEnum.Due && document.dateDue) {
      return {
        date: new Date(document.dateDue),
        basisLabel: optionLabel(MATCH_DATE_BASIS_OPTIONS, basis).toLowerCase(),
      };
    }
    if (basis === MatchDocumentDateBasisEnum.Issued) {
      const raw = document.dateIssued ?? document.createdAt;
      if (raw) {
        return {
          date: new Date(raw),
          basisLabel: optionLabel(
            MATCH_DATE_BASIS_OPTIONS,
            basis,
          ).toLowerCase(),
        };
      }
    }
    if (basis === MatchDocumentDateBasisEnum.UploadDate && document.createdAt) {
      return {
        date: new Date(document.createdAt),
        basisLabel: optionLabel(MATCH_DATE_BASIS_OPTIONS, basis).toLowerCase(),
      };
    }
  }

  return { date: null, basisLabel: "bill date" };
}

function dateWithinWindow(
  documentDate: Date,
  bankDate: Date,
  daysBefore: number,
  daysAfter: number,
): boolean {
  const deltaDays = Math.round(
    (Date.UTC(
      documentDate.getFullYear(),
      documentDate.getMonth(),
      documentDate.getDate(),
    ) -
      Date.UTC(
        bankDate.getFullYear(),
        bankDate.getMonth(),
        bankDate.getDate(),
      )) /
      86_400_000,
  );
  return -daysAfter <= deltaDays && deltaDays <= daysBefore;
}

function formatDateDeltaPhrase(documentDate: Date, bankDate: Date): string {
  const deltaDays = Math.round(
    (Date.UTC(
      documentDate.getFullYear(),
      documentDate.getMonth(),
      documentDate.getDate(),
    ) -
      Date.UTC(
        bankDate.getFullYear(),
        bankDate.getMonth(),
        bankDate.getDate(),
      )) /
      86_400_000,
  );
  if (deltaDays === 0) {
    return "Same day";
  }
  const absDays = Math.abs(deltaDays);
  const dayLabel = `${absDays}d`;
  if (deltaDays < 0) {
    return `${dayLabel} after`;
  }
  return `${dayLabel} before`;
}

function formatDateRulePhrase(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string {
  const before = definition.dateWindowDaysBefore ?? 0;
  const after = definition.dateWindowDaysAfter ?? 0;
  if (before === 0 && after === 0) {
    return "0d";
  }
  if (before === 0 && after > 0) {
    return `−${after}d…0d`;
  }
  if (after === 0 && before > 0) {
    return `0d…+${before}d`;
  }
  if (before > 0 && after > 0) {
    return `−${after}d…+${before}d`;
  }
  return "0d";
}

function amountMatchPhrase(
  amountDelta: number | null,
  amountDeltaLabel?: string,
): string {
  if (amountDelta == null) {
    return "—";
  }
  if (amountDelta === 0) {
    return "Exact";
  }
  return amountDeltaLabel ?? "—";
}

function amountRulePhrase(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string | null {
  const toleranceLabel = formatToleranceValue(definition);
  if (toleranceLabel == null) {
    return null;
  }
  return `±${toleranceLabel}`;
}

function isWordingMatched(matched: Set<string>): boolean {
  return (
    matched.has("wording") ||
    matched.has("reference") ||
    matched.has("invoice_number") ||
    matched.has("external_id")
  );
}

function wordingMatchCell(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
  matched: Set<string>,
): string {
  if (matched.has("reference") || matched.has("invoice_number")) {
    return "Reference Match";
  }
  if (matched.has("external_id")) {
    return "ID Match";
  }
  if (matched.has("wording")) {
    return "Similar";
  }
  if (
    definition.textMatchMode == null ||
    definition.textMatchMode === MatchDocumentTextMatchModeEnum.Off
  ) {
    return "—";
  }
  return "Different";
}

function wordingRuleCell(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string {
  const mode = definition.textMatchMode;
  if (mode == null || mode === MatchDocumentTextMatchModeEnum.Off) {
    return "—";
  }
  const label = optionLabel(TEXT_MATCH_MODE_OPTIONS, mode);
  const threshold = definition.similarityThreshold;
  if (
    mode === MatchDocumentTextMatchModeEnum.Semantic &&
    threshold != null &&
    Number.isFinite(Number(threshold))
  ) {
    const percent = Math.round(Number(threshold) * 100);
    return `≥${percent}%`;
  }
  if (mode === MatchDocumentTextMatchModeEnum.Reference) {
    return "#";
  }
  if (mode === MatchDocumentTextMatchModeEnum.Fuzzy) {
    return "~";
  }
  return label;
}

function wordingBankCell(bankDescription?: string | null): string {
  const text = bankDescription?.trim();
  return text || "—";
}

function wordingDocCell(document: LinkedDocument): string {
  const text = document.description?.trim() || document.title?.trim() || null;
  return text || "—";
}

function configuredMatchDocumentCriteriaFromDefinition(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string[] {
  const criteria: string[] = [];
  if (definition.amountTolerance != null) {
    criteria.push("amount");
  }
  if (
    definition.dateWindowDaysBefore != null &&
    definition.dateWindowDaysAfter != null
  ) {
    criteria.push("date_window");
  }
  if (
    definition.textMatchMode != null &&
    definition.textMatchMode !== MatchDocumentTextMatchModeEnum.Off
  ) {
    criteria.push("wording");
  }
  return criteria;
}

function resolveConfiguredCriteria(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
  configuredCriteria?: string[],
): string[] {
  const order = ["amount", "date_window", "wording"];
  const source =
    configuredCriteria && configuredCriteria.length > 0
      ? configuredCriteria
      : configuredMatchDocumentCriteriaFromDefinition(definition);
  return order.filter((id) => source.includes(id));
}

function matchDocumentDateBasisProse(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string {
  const bases = definition.matchDateBasis ?? [
    MatchDocumentDateBasisEnum.Issued,
  ];
  const primary = bases.filter(
    (basis) => basis !== MatchDocumentDateBasisEnum.UploadDate,
  );
  const basis = primary[0] ?? MatchDocumentDateBasisEnum.UploadDate;
  return optionLabel(MATCH_DATE_BASIS_OPTIONS, basis).toLowerCase();
}

function formatDateWindowNatural(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string {
  const before = definition.dateWindowDaysBefore ?? 0;
  const after = definition.dateWindowDaysAfter ?? 0;
  const basis = matchDocumentDateBasisProse(definition);
  if (before === 0 && after === 0) {
    return `Same ${basis}`;
  }
  if (before === 0 && after > 0) {
    const dayLabel = after === 1 ? "day" : "days";
    return `${upperFirst(basis)} up to ${after} ${dayLabel} after`;
  }
  if (after === 0 && before > 0) {
    const dayLabel = before === 1 ? "day" : "days";
    return `${upperFirst(basis)} up to ${before} ${dayLabel} before`;
  }
  const beforeLabel = before === 1 ? "day" : "days";
  const afterLabel = after === 1 ? "day" : "days";
  return `${upperFirst(basis)} from ${before} ${beforeLabel} before to ${after} ${afterLabel} after`;
}

function matchDocumentAmountCriterionProse(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string | null {
  const tolerance = formatToleranceValue(definition);
  if (tolerance == null) {
    return null;
  }
  return `Amount within ${tolerance}`;
}

function matchDocumentWordingCriterionProse(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
): string | null {
  const mode = definition.textMatchMode;
  if (mode == null || mode === MatchDocumentTextMatchModeEnum.Off) {
    return null;
  }
  if (mode === MatchDocumentTextMatchModeEnum.Reference) {
    return "Same reference";
  }
  if (mode === MatchDocumentTextMatchModeEnum.Semantic) {
    const threshold = definition.similarityThreshold;
    if (threshold != null && Number.isFinite(Number(threshold))) {
      const percent = Math.round(Number(threshold) * 100);
      return `Description at least ${percent}% similar`;
    }
    return "Similar description";
  }
  if (mode === MatchDocumentTextMatchModeEnum.Fuzzy) {
    return "Similar description";
  }
  return "Same description";
}

/** What a match-document rule looks for, one criterion per line. */
export function matchDocumentFindCriteriaLines(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
  configuredCriteria?: string[],
): string[] {
  const lines: string[] = [];
  for (const criterionId of resolveConfiguredCriteria(
    definition,
    configuredCriteria,
  )) {
    if (criterionId === "date_window") {
      lines.push(formatDateWindowNatural(definition));
    }
    if (criterionId === "amount") {
      const amount = matchDocumentAmountCriterionProse(definition);
      if (amount) {
        lines.push(amount);
      }
    }
    if (criterionId === "wording") {
      const wording = matchDocumentWordingCriterionProse(definition);
      if (wording) {
        lines.push(wording);
      }
    }
  }
  return lines;
}

export type BreakdownColumn = {
  id: string;
  label: string;
  bank: string | null;
  doc: string | null;
  match: string | null;
  rule: string | null;
  matched: boolean;
};

export function buildBreakdownColumns(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
  document: LinkedDocument | null,
  bankAmount: number,
  bankDate: string | null | undefined,
  bankDescription: string | null | undefined,
  matchedSignals: string[],
  configuredCriteria?: string[],
): BreakdownColumn[] {
  if (!document) {
    return [];
  }
  const matched = new Set(matchedSignals);
  const criteria = resolveConfiguredCriteria(definition, configuredCriteria);
  const columns: BreakdownColumn[] = [];

  for (const criterionId of criteria) {
    if (!MATCH_RESULT_CRITERIA.has(criterionId)) {
      continue;
    }

    if (criterionId === "amount") {
      const compared = documentCompareAmount(document, definition);
      const toleranceLabel = formatToleranceValue(definition);
      if (compared == null || toleranceLabel == null) {
        continue;
      }
      const bankLabel = Utils.fCurrency(Math.abs(bankAmount), false, true);
      // In its own currency, and what that comes to in the payment's - not
      // compared at all where the day's rate is not published yet.
      const unrated =
        document.exchangeRateSource != null && document.exchangeRate == null;
      const docLabel =
        document.exchangeRate != null
          ? `${document.currency} ${compared.own.toFixed(2)} ≈ ${Utils.fCurrency(compared.inPayment, false, true)}`
          : unrated
            ? `${document.currency} ${compared.own.toFixed(2)}`
            : Utils.fCurrency(compared.own, false, true);
      const { amountDelta, amountDeltaLabel } = amountDeltaFromCompare(
        compared.inPayment,
        bankAmount,
      );
      const matchedAmount = amountWithinTolerance(
        amountDelta ?? 0,
        bankAmount,
        definition,
      );
      columns.push({
        id: "amount",
        label: "Amount",
        bank: bankLabel,
        doc: docLabel,
        match: unrated
          ? "No rate"
          : amountMatchPhrase(amountDelta, amountDeltaLabel),
        rule: amountRulePhrase(definition),
        matched: !unrated && matchedAmount,
      });
      continue;
    }

    if (criterionId === "date_window") {
      const { date: docDate } = documentCompareDate(document, definition);
      const bank = bankDate ? new Date(bankDate) : null;
      if (
        !docDate ||
        !bank ||
        Number.isNaN(docDate.getTime()) ||
        Number.isNaN(bank.getTime())
      ) {
        continue;
      }
      const bankLabel = Utils.fDate(bank);
      const docLabel = Utils.fDate(docDate);
      const deltaPhrase = formatDateDeltaPhrase(docDate, bank);
      const before = definition.dateWindowDaysBefore ?? 0;
      const after = definition.dateWindowDaysAfter ?? 0;
      const matchedDate =
        matched.has("date_window") ||
        dateWithinWindow(docDate, bank, before, after);
      columns.push({
        id: "date_window",
        label: "Date",
        bank: bankLabel,
        doc: docLabel,
        match: deltaPhrase,
        rule: formatDateRulePhrase(definition),
        matched: matchedDate,
      });
      continue;
    }

    if (criterionId === "wording") {
      columns.push({
        id: "wording",
        label: "Description",
        bank: wordingBankCell(bankDescription),
        doc: wordingDocCell(document),
        match: wordingMatchCell(definition, matched),
        rule: wordingRuleCell(definition),
        matched: isWordingMatched(matched),
      });
    }
  }

  return columns;
}

export function countMatchDocumentCriteriaBuckets(
  definition: RuleDefinitionSuggestionMatchDocumentFragment,
  matchedSignals: string[],
  configuredCriteria?: string[],
  document: LinkedDocument | null = null,
  bankAmount = 0,
  bankDate?: string | null,
  bankDescription?: string | null,
): { matched: number; total: number } {
  const columns = buildBreakdownColumns(
    definition,
    document,
    bankAmount,
    bankDate,
    bankDescription,
    matchedSignals,
    configuredCriteria,
  );
  if (columns.length === 0) {
    return { matched: 0, total: 0 };
  }
  return {
    matched: columns.filter((column) => column.matched).length,
    total: columns.length,
  };
}
